<template>
  <div class="season-form">
    <!-- Step 1: Season Data -->
    <div v-if="step === 1">      
      <h2 v-if="hasTitle">{{ isEditMode ? t('seasonForm.editTitle') : t('seasonForm.newTitle') }}</h2>
      <form @submit.prevent="handleSubmit">
        <!-- Mode Selection -->
        <div class="form-group">
          <label class="form-label">{{ t('seasonForm.modeLabel') }}</label>
          <div class="radio-group">
            <label class="radio-label">
              <input type="radio" :value="false" v-model="isCustomMode" />
              {{ t('seasonForm.fullYearMode') }}
            </label>
            <label class="radio-label">
              <input type="radio" :value="true" v-model="isCustomMode" />
              {{ t('seasonForm.customMode') }}
            </label>
          </div>
        </div>

        <!-- Year Selection (Full Year Mode) -->
        <AeSelect
          v-if="!isCustomMode"
          v-model="selectedYear"
          :options="yearOptions"
          :label="t('seasonForm.yearLabel')"
          :placeholder="t('seasonForm.yearPlaceholder')"
          @change="handleYearChange"
        />

        <!-- Custom Name (Custom Mode) -->
        <AeInput
          v-if="isCustomMode"
          v-model="formData.name"
          :label="t('seasonForm.seasonNameLabel')"
          :placeholder="t('seasonForm.seasonNamePlaceholder')"
          required
          :error="errors.name"
        />

        <!-- Date Range -->
        <div class="form-row">
          <AeInput
            v-model="formData.date_from"
            type="date"
            :label="t('seasonForm.dateFromLabel')"
            :disabled="!isCustomMode"
            required
            :error="errors.date_from"
          />

          <AeInput
            v-model="formData.date_to"
            type="date"
            :label="t('seasonForm.dateToLabel')"
            :disabled="!isCustomMode"
            required
            :error="errors.date_to"
          />
        </div>

        <!-- Validation Errors -->
        <div v-if="validationErrors.length > 0" class="validation-errors">
          <p v-for="(error, index) in validationErrors" :key="index" class="error">
            {{ error }}
          </p>
        </div>

        <!-- Actions -->
        <div class="form-actions">
          <AeButton variant="secondary" type="button" @click="handleCancel">
            {{ t('seasonForm.cancel') }}
          </AeButton>
          <AeButton variant="primary" type="submit" :disabled="loading">
            {{ loading ? t('seasonForm.saving') : (isEditMode ? t('seasonForm.saveChanges') : t('seasonForm.createSeason')) }}
          </AeButton>
        </div>
      </form>
    </div>

    <!-- Step 2: Copy fields from previous season -->
    <div v-if="step === 2">
      <h2>{{ t('seasonForm.copyStepTitle') }}</h2>
      <p class="copy-hint">{{ t('seasonForm.copyStepHint') }}</p>

      <div class="copy-fields-list">
        <div
          v-for="(item, index) in copyFieldItems"
          :key="index"
          class="copy-field-item"
        >
          <input type="checkbox" v-model="item.selected" />
          <span class="copy-field-plot">{{ item.plot_name }}</span>
          <span class="copy-field-arrow">→</span>
          <AeSelect
            v-model="item.id_crop"
            :options="cropOptions"
            class="copy-field-crop-select"
            searchable
            :placeholder="t('seasonForm.cropPlaceholder')"
            @update:modelValue="onCropChanged(index, $event)"
          />
        </div>
      </div>

      <div v-if="copyLoading" class="copy-loading">
        <p>{{ t('seasonForm.copyingInProgress') }}</p>
      </div>

      <div class="form-actions">
        <AeButton variant="secondary" @click="skipCopy">
          {{ t('seasonForm.skip') }}
        </AeButton>
        <AeButton variant="primary" @click="doCopy" :disabled="copyLoading || selectedCopyCount === 0">
          {{ t('seasonForm.copySelected', { count: selectedCopyCount }) }}
        </AeButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDbStore } from '../stores/dbStore'
import { usePoderiStore } from '../stores/poderiStore'
import { AeInput, AeButton, AeSelect } from '@aevue/components'

const { t } = useI18n()

const props = defineProps({
  seasonId: {
    type: [String, Number],
    default: null
  },
  farmCenterId: {
    type: [String, Number],
    required: true
  },
  previousSeasonFields: {
    type: Array,
    default: () => []
  },
  hasTitle: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['submit', 'cancel'])

const dbStore = useDbStore()
const poderiStore = usePoderiStore()
const loading = ref(false)
const isCustomMode = ref(false)
const selectedYear = ref(null)
const validationErrors = ref([])

const step = ref(1)
const createdSeasonData = ref(null)
const copyFieldItems = ref([])
const copyLoading = ref(false)
const cropOptions = ref([])

const isEditMode = computed(() => !!props.seasonId)

const selectedCopyCount = computed(() => copyFieldItems.value.filter(i => i.selected).length)

// Generate year options (current year +/- 5 years)
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear()
  const years = []
  for (let i = -5; i <= 5; i++) {
    const year = currentYear + i
    years.push({ value: year, label: year.toString() })
  }
  return years
})

const formData = ref({
  name: '',
  date_from: '',
  date_to: '',
  id_farm_center: props.farmCenterId
})

const errors = ref({
  name: '',
  date_from: '',
  date_to: ''
})

// Watch year selection to auto-fill dates
watch(selectedYear, (year) => {
  if (year && !isCustomMode.value) {
    formData.value.name = `${year}`
    formData.value.date_from = `${year}-01-01`
    formData.value.date_to = `${year}-12-31`
  }
})

// Watch mode change
watch(isCustomMode, (custom) => {
  if (!custom && selectedYear.value) {
    handleYearChange()
  }
})

onMounted(async () => {
  if (isEditMode.value) {
    loading.value = true
    try {
      const result = await dbStore.selectOne('season', props.seasonId)

      if (result.ok && result.data) {
        const season = result.data
        formData.value = {
          name: season.name || season.season_name || '',
          date_from: season.date_from || '',
          date_to: season.date_to || '',
          id_farm_center: props.farmCenterId
        }

        // Check if it's a full year season
        const yearMatch = formData.value.name.match(/^(?:Anno )?(\d{4})$/)
        if (yearMatch) {
          selectedYear.value = parseInt(yearMatch[1])
          isCustomMode.value = false
        } else {
          isCustomMode.value = true
        }
      }
    } catch (error) {
      console.error('Error loading season:', error)
      validationErrors.value = [t('seasonForm.loadError')]
    } finally {
      loading.value = false
    }
  } else {
    // Default to current year for new seasons
    selectedYear.value = new Date().getFullYear()
  }
})

function handleYearChange() {
  if (selectedYear.value) {
    formData.value.name = `${selectedYear.value}`
    formData.value.date_from = `${selectedYear.value}-01-01`
    formData.value.date_to = `${selectedYear.value}-12-31`
  }
}

function validateForm() {
  validationErrors.value = []
  errors.value = {
    name: '',
    date_from: '',
    date_to: ''
  }

  let isValid = true

  if (!formData.value.name?.trim()) {
    errors.value.name = t('seasonForm.nameRequired')
    isValid = false
  }

  if (!formData.value.date_from) {
    errors.value.date_from = t('seasonForm.dateFromRequired')
    isValid = false
  }

  if (!formData.value.date_to) {
    errors.value.date_to = t('seasonForm.dateToRequired')
    isValid = false
  }

  // Validate date range
  if (formData.value.date_from && formData.value.date_to) {
    const from = new Date(formData.value.date_from)
    const to = new Date(formData.value.date_to)

    if (from >= to) {
      errors.value.date_to = t('seasonForm.dateToAfterFrom')
      isValid = false
    }
  }

  return isValid
}

async function checkOverlappingSeasons() {
  // Get all seasons for this farm center
  const result = await dbStore.select('season', {
    id_farm_center: props.farmCenterId
  })

  if (!result.ok || !result.data) return null

  const newFrom = new Date(formData.value.date_from)
  const newTo = new Date(formData.value.date_to)

  // Find overlapping seasons (excluding current season if editing)
  const overlapping = result.data.find(season => {
    // Skip if this is the season we're editing
    if (isEditMode.value && season.id_season === props.seasonId) {
      return false
    }

    const existingFrom = new Date(season.date_from)
    const existingTo = new Date(season.date_to)

    // Check if date ranges overlap
    // Two ranges overlap if: start1 <= end2 AND start2 <= end1
    return newFrom <= existingTo && existingFrom <= newTo
  })

  return overlapping
}

async function handleSubmit() {
  if (!validateForm()) return

  loading.value = true

  try {
    // Check for overlapping seasons
    const overlappingSeason = await checkOverlappingSeasons()

    if (overlappingSeason) {
      const overlapFrom = new Date(overlappingSeason.date_from).toLocaleDateString('it-IT')
      const overlapTo = new Date(overlappingSeason.date_to).toLocaleDateString('it-IT')
      validationErrors.value = [
        t('seasonForm.overlapMessage', {
          name: overlappingSeason.name || overlappingSeason.season_name,
          from: overlapFrom,
          to: overlapTo
        })
      ]
      loading.value = false
      return
    }

    const data = {
      name: formData.value.name.trim(),
      season_name: formData.value.name.trim(), // alias for compatibility
      date_from: formData.value.date_from,
      date_to: formData.value.date_to,
      id_farm_center: props.farmCenterId,
      year: selectedYear.value || new Date(formData.value.date_from).getFullYear()
    }

    let result
    if (isEditMode.value) {
      result = await dbStore.update('season', props.seasonId, data)
    } else {
      result = await dbStore.insert('season', data)
    }

    if (result.ok) {
      // If creating and there are previous season fields, show step 2
      if (!isEditMode.value && props.previousSeasonFields.length > 0) {
        createdSeasonData.value = result.data
        // Load crops for the selector
        cropOptions.value = await poderiStore.getCropOptions()
        // Prepare copy items from previous season fields
        copyFieldItems.value = props.previousSeasonFields.map(f => ({
          id_plot: f.id_plot,
          plot_name: f.plot_name,
          id_crop: f.id_crop,
          crop_name: f.crop_name,
          selected: true
        }))
        step.value = 2
      } else {
        emit('submit', result.data)
      }
    } else {
      validationErrors.value = [result.message || t('seasonForm.saveError')]
    }
  } catch (error) {
    console.error('Error saving season:', error)
    validationErrors.value = [error.message || t('seasonForm.saveError')]
  } finally {
    loading.value = false
  }
}

function onCropChanged(index, newCropId) {
  const opt = cropOptions.value.find(o => o.value === newCropId)
  if (opt) {
    copyFieldItems.value[index].crop_name = opt.label
  }
}

function skipCopy() {
  emit('submit', createdSeasonData.value)
}

async function doCopy() {
  copyLoading.value = true
  try {
    // createdSeasonData.value is the primary key (GUID string) from dbStore.insert
    const newSeasonId = createdSeasonData.value
    if (!newSeasonId) {
      console.error('No season ID found in created data:', createdSeasonData.value)
      emit('submit', createdSeasonData.value)
      return
    }

    const selectedItems = copyFieldItems.value.filter(i => i.selected)
    const fieldsToCreate = selectedItems.map(item => ({
      id_farm_center: props.farmCenterId,
      id_plot: item.id_plot,
      id_season: newSeasonId,
      id_crop: item.id_crop
    }))

    await poderiStore.createFields(fieldsToCreate)
    emit('submit', createdSeasonData.value)
  } catch (error) {
    console.error('Error copying fields:', error)
    emit('submit', createdSeasonData.value)
  } finally {
    copyLoading.value = false
  }
}

function handleCancel() {
  emit('cancel')
}
</script>

<style scoped>
.season-form {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  max-width: 600px;
  margin: 0 auto;
}

.season-form h2 {
  margin: 0 0 1.5rem;
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #333;
}

.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.radio-label input[type="radio"] {
  cursor: pointer;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.validation-errors {
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  padding: 1rem;
}

.error {
  color: var(--danger-color, #e53e3e);
  font-size: 0.875rem;
  margin: 0.25rem 0;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

/* Step 2: Copy fields */
.copy-hint {
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 1rem;
}

.copy-fields-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 1rem;
}

.copy-field-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f9f9f9;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 0.875rem;
}

.copy-field-item:hover {
  background: #f0f0f0;
}

.copy-field-item input[type="checkbox"] {
  cursor: pointer;
  flex-shrink: 0;
}

.copy-field-plot {
  font-weight: 500;
  color: #333;
}

.copy-field-arrow {
  color: #999;
}

.copy-field-crop {
  color: #667eea;
  font-weight: 500;
}

.copy-field-crop-select {
  flex: 1;
  min-width: 150px;
}

.copy-loading {
  text-align: center;
  padding: 1rem;
  color: #666;
  font-size: 0.875rem;
}
</style>
